import { useQuery } from '@tanstack/react-query';
import {
  AccessorKeyColumnDef,
  createColumnHelper,
  flexRender,
  getCoreRowModel,
  useReactTable
} from '@tanstack/react-table';
import { Card, Col, Container, Row, Table } from 'react-bootstrap';
import { Route, Routes } from 'react-router-dom';

import { getEmpties, getEmptiesByCustomer } from '../../apis/queries';
import EmptyTableState from '../../components/EmptyState/EmptyStateTable';
import Loader from '../../components/Loader';
import { EmptiesByCustomer, Empty } from '../../types';
import AnalyticsHeader from '../../widgets/Header';

export const GET_EMPTIES_KEY = 'empties';
export const GET_EMPTIES_CUSTOMERS_KEY = 'emptiesCustomers';
const EmptiesProductsTab = () => {
  const { data, isLoading } = useQuery({ queryKey: [GET_EMPTIES_KEY], queryFn: getEmpties });
  const empties: Empty[] = data?.results || [];

  const columnHelper = createColumnHelper<Empty>();

  const columns = [
    columnHelper.accessor('product.name', {
      header: 'Product ID',
      cell: info => info.getValue(),
      footer: info => info.column.id
    }),
    columnHelper.accessor('total', {
      header: 'Total',
      cell: info => info.getValue(),
      footer: info => info.column.id
    })
  ];

  return (
    <div className="main-content">
      <AnalyticsHeader title="Empties" tabs={tabRoutes} />
      <Container fluid>
        <Row className="justify-content-center">
          <Col xs={12}>
            {isLoading ? (
              <Loader />
            ) : empties && empties?.length > 0 ? (
              <TableList empties={empties!} columns={columns} />
            ) : (
              <EmptyTableState message="No customer empties yet." />
            )}{' '}
          </Col>
        </Row>
      </Container>
    </div>
  );
};

const EmptiesCustomersTab = () => {
  const { data, isLoading } = useQuery({
    queryKey: [GET_EMPTIES_CUSTOMERS_KEY],
    queryFn: getEmptiesByCustomer
  });
  const empties = data;

  const columnHelper = createColumnHelper<EmptiesByCustomer>();

  const columns = [
    columnHelper.accessor('customer.name', {
      header: 'Customer Name',
      cell: info => info.getValue(),
      footer: info => info.column.id
    }),
    columnHelper.accessor('customer.phoneNumber', {
      header: 'Phone Number',
      cell: info => info.getValue(),
      footer: info => info.column.id
    }),
    columnHelper.accessor('customer.customerType', {
      header: 'Customer Type',
      cell: info => info.getValue(),
      footer: info => info.column.id
    }),
    columnHelper.accessor('total', {
      header: 'Total',
      cell: info => info.getValue(),
      footer: info => info.column.id
    })
  ];

  return (
    <div className="main-content">
      <AnalyticsHeader title="Empties" tabs={tabRoutes} />
      <Container fluid>
        <Row className="justify-content-center">
          <Col xs={12}>
            {isLoading ? (
              <Loader />
            ) : empties && empties?.length > 0 ? (
              <TableList empties={empties!} columns={columns} />
            ) : (
              <EmptyTableState message="No customer empties yet." />
            )}
          </Col>
        </Row>
      </Container>
    </div>
  );
};

const tabRoutes = [
  {
    title: 'Products',
    path: '',
    tabLink: '/empties',
    element: <EmptiesProductsTab />
  },
  {
    title: 'Customer',
    path: 'customers',
    tabLink: '/empties/customers',
    element: <EmptiesCustomersTab />
  }
];

const Empties = () => {
  return (
    <div className="main-content">
      <Routes>
        {tabRoutes.map(({ path, element, title }) => (
          <Route key={title} path={path} element={element} />
        ))}
      </Routes>
    </div>
  );
};

export default Empties;

interface TableListProps<T extends Record<string, unknown>> {
  empties: T[];
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  columns: AccessorKeyColumnDef<T, any>[];
}

const TableList = <T extends Record<string, unknown>>({ empties, columns }: TableListProps<T>) => {
  const tableInstance = useReactTable({ data: empties!, columns, getCoreRowModel: getCoreRowModel() });

  return (
    <Card>
      <Table size="sm" className="card-table table-nowrap" hover responsive>
        <thead>
          {tableInstance.getHeaderGroups().map(headerGroup => {
            return (
              <tr key={headerGroup.id}>
                {headerGroup.headers.map(header => {
                  return (
                    <th colSpan={header.colSpan} key={header.id}>
                      {header.isPlaceholder ? null : flexRender(header.column.columnDef.header, header.getContext())}
                    </th>
                  );
                })}
              </tr>
            );
          })}
        </thead>
        <tbody>
          {tableInstance.getRowModel().rows.map(row => {
            return (
              <tr key={row.id}>
                {row.getVisibleCells().map(cell => {
                  return <td key={cell.id}>{flexRender(cell.column.columnDef.cell, cell.getContext())}</td>;
                })}
              </tr>
            );
          })}
        </tbody>
      </Table>
    </Card>
  );
};
